import React, { useEffect } from "react";
import "./dealMemberRow.scss";
import { DealLegalEntity, LegalEntityType } from "../../../api/generated-api";
import { getUserName } from "../../../helpers/utils";
import Button from "../../common/forms/button";
import RemoveIcon from "../../common/icons/removeIcon";
import COLORS from "../../../colors";
import { toValidIdentifier } from "../../../helpers/utilsTS";
import SignatureIcon from "../../common/icons/signatureIcon";
import { Box, useTheme } from "@mui/material";

type DealMemberRowProps = {
  dealMember: DealLegalEntity;
  showHeader?: boolean;
  editable?: boolean;
  deletable: boolean;
  invitable: boolean;
  canConfigureSignatureBlock?: boolean;
  onInvite: () => void;
  onRemove: () => void;
  onConfigureSignatureBlock?: () => void;
};

type CompanyMemberPositions = {
  [K in keyof typeof LegalEntityType]: ReturnType<typeof toValidIdentifier>;
}[keyof typeof LegalEntityType];

type DealMemberPositions = CompanyMemberPositions | "investor";

const DealMemberRow = ({
  dealMember,
  showHeader = false,
  editable = false,
  deletable,
  invitable,
  canConfigureSignatureBlock,
  onInvite,
  onRemove,
  onConfigureSignatureBlock,
}: DealMemberRowProps) => {
  const [userStatus, setUserStatus] = React.useState<
    "person" | "invited" | "user"
  >(null);
  const [dealMemberRole, setDealMemberRole] =
    React.useState<DealMemberPositions>(null);

  const theme = useTheme();

  useEffect(() => {
    if (!dealMember.person.user) {
      setUserStatus("person");
    } else if (dealMember.person.user && !dealMember.person.user?.verified) {
      setUserStatus("invited");
    } else if (dealMember.person.user && dealMember.person.user?.verified) {
      setUserStatus("user");
    }

    setDealMemberRole(getDealMemberRole(dealMember));
  }, [dealMember]);

  const getDealMemberRole = (dealMember: DealLegalEntity) => {
    if (
      dealMember.legalEntityType === LegalEntityType.Chief_Executive_Officer
    ) {
      return "ceo";
    } else if (dealMember.legalEntityType === LegalEntityType.Secretary) {
      return "secretary";
    } else if (dealMember.legalEntityType === LegalEntityType.Board_Member) {
      return "board-member";
    } else if (
      dealMember.legalEntityType === LegalEntityType.Key_Common_Holder
    ) {
      return "key-common-holder";
    } else if (dealMember.legalEntityType === LegalEntityType.Company_Counsel) {
      return "company-counsel";
    } else {
      return "investor";
    }
  };

  return (
    <>
      {showHeader && (
        <div className="deal-member-header-row">
          <div className="name-header">Name</div>
          <div className="email-header">Email</div>
          <div className="actions-header"></div>
        </div>
      )}
      <div
        className="deal-member-row"
        data-testid={`deal-member-${dealMemberRole}-row`}
      >
        <div className="name">
          {getUserName(dealMember.person)}
          {"position" in dealMember &&
            (dealMember.position === LegalEntityType.Chief_Executive_Officer ||
              dealMember.position === LegalEntityType.Secretary) && (
              <div className="sub-label">{dealMember.position}</div>
            )}
        </div>
        <div className="email">
          <a href={`mailto:${dealMember.person.email}`}>
            {dealMember.person.email}
          </a>
        </div>
        <div className="actions">
          {editable && (
            <>
              <div className="action-container">
                {invitable && userStatus === "person" && (
                  <Button
                    data-testid={`button-invite-${dealMemberRole}-member`}
                    size="medium-min"
                    type="secondary"
                    onClick={onInvite}
                  >
                    invite
                  </Button>
                )}
                {invitable && userStatus === "invited" && (
                  <Button
                    data-testid={`button-invite-${dealMemberRole}-member`}
                    size="medium-min"
                    type="secondary"
                    onClick={onInvite}
                  >
                    send reminder
                  </Button>
                )}
              </div>
              <div className="action-container">
                {canConfigureSignatureBlock && (
                  <Box mr={1} data-testid="configure-signature-icon">
                    <SignatureIcon
                      fill={theme.palette.secondary.darker}
                      onClick={onConfigureSignatureBlock}
                    />
                  </Box>
                )}

                {deletable && (
                  <RemoveIcon
                    data-testid={"remove-deal-member-button"}
                    fill={COLORS.SECONDARY_COLOR_700}
                    onClick={onRemove}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DealMemberRow;
